import React, { useState } from "react";
import Button, { ButtonTypes } from "../../../../../components/Button";
import { CardFooterLink } from "../../../../../components/CardFooterLink";
import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { KebabButton } from "../../../../../components/KebabButton";
import { lockedActions } from "../../../../../components/SettlementLock";
import { Transaction } from "../../../../../services/transactions";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  SectionCard,
  SectionHeader,
  TitleBlock,
  SectionTitle,
  SubtitleText,
} from "../../styles";
import {
  HeaderActions,
  ViewAllLink,
  AddButtonFullLabel,
  AddButtonShortLabel,
  TypePill,
  AmountText,
  RowsList,
  TransactionRow,
  DateText,
  CategoryStack,
  CategoryName,
  SubCategoryName,
  NoteText,
  NoteStack,
  AmountCell,
  ActionsCell,
  CardsList,
  TransactionCard,
  CardTopRow,
  CardTopLeft,
  CardMidRow,
  CardCategory,
  CardBottomRow,
  CardBottomNote,
  CardBottomDot,
  EmptyWrapper,
  SkeletonRow,
} from "./styles";
import { TransactionActionSheet } from "./TransactionActionSheet";
import { RecentTransactionsSectionProps } from "./types";
import {
  buildDropdownActions,
  getAmountDisplay,
  getPillLabel,
  getPillVariant,
} from "./utils";

export const RecentTransactionsSection: React.FC<
  RecentTransactionsSectionProps
> = ({
  transactions,
  totalRows,
  isLoading,
  onAdd,
  onEdit,
  onDuplicate,
  onDelete,
  onViewAll,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const addButtonSize = isCompact ? "small" : "medium";
  const [actionSheetTx, setActionSheetTx] = useState<Transaction | null>(null);

  const openActionSheet = (tx: Transaction) => setActionSheetTx(tx);
  const closeActionSheet = () => setActionSheetTx(null);

  const renderEmpty = (
    <EmptyWrapper>No transactions yet for this account.</EmptyWrapper>
  );

  const renderSkeleton = (
    <>
      <SkeletonRow />
      <SkeletonRow />
      <SkeletonRow />
    </>
  );

  const renderDesktopRows = () => (
    <RowsList>
      {transactions.map((tx) => {
        const pillVariant = getPillVariant(tx.transactionType);
        const pillLabel = getPillLabel(tx.transactionType);
        const { sign, variant } = getAmountDisplay(tx);
        const { lock, actions } = lockedActions({
          settlement: tx.settlement,
          cashbackExpenseId: tx.cashbackExpenseId,
          actions: (
            <ActionsDropdown
              compact
              size="sm"
              dropdownActions={buildDropdownActions(
                tx,
                onEdit,
                onDuplicate,
                onDelete,
              )}
            />
          ),
        });
        return (
          <TransactionRow key={tx.id}>
            <TypePill $variant={pillVariant}>{pillLabel}</TypePill>
            <DateText>
              {formatDate(tx.transactionDate, DateFormat.DMY_SPACE)}
            </DateText>
            <CategoryStack>
              <CategoryName>{tx.categoryName ?? pillLabel}</CategoryName>
              {tx.subCategoryName && (
                <SubCategoryName>{tx.subCategoryName}</SubCategoryName>
              )}
            </CategoryStack>
            <NoteStack>
              <NoteText>{tx.note ?? ""}</NoteText>
              {lock}
            </NoteStack>
            <AmountCell>
              <AmountText $variant={variant}>
                {sign}
                {formatCurrency(tx.amount)}
              </AmountText>
            </AmountCell>
            <ActionsCell>{actions}</ActionsCell>
          </TransactionRow>
        );
      })}
    </RowsList>
  );

  const renderMobileCards = () => (
    <CardsList>
      {transactions.map((tx) => {
        const pillVariant = getPillVariant(tx.transactionType);
        const pillLabel = getPillLabel(tx.transactionType);
        const { sign, variant } = getAmountDisplay(tx);
        const hasSubcategory = !!tx.subCategoryName;
        const hasNote = !!tx.note;
        const { lock, actions: kebab } = lockedActions({
          settlement: tx.settlement,
          cashbackExpenseId: tx.cashbackExpenseId,
          actions: (
            <KebabButton
              size="small"
              aria-label="Transaction actions"
              onClick={() => openActionSheet(tx)}
            />
          ),
        });
        return (
          <TransactionCard key={tx.id}>
            <CardTopRow>
              <CardTopLeft>
                <TypePill $variant={pillVariant}>{pillLabel}</TypePill>
                <DateText>
                  {formatDate(tx.transactionDate, DateFormat.DM_SPACE)}
                </DateText>
              </CardTopLeft>
              {kebab}
            </CardTopRow>
            <CardMidRow>
              <CardCategory>{tx.categoryName ?? pillLabel}</CardCategory>
              <AmountText $variant={variant}>
                {sign}
                {formatCurrency(tx.amount)}
              </AmountText>
            </CardMidRow>
            {(hasSubcategory || hasNote) && (
              <CardBottomRow>
                {hasSubcategory && tx.subCategoryName}
                {hasSubcategory && hasNote && <CardBottomDot>·</CardBottomDot>}
                {hasNote && <CardBottomNote>{tx.note}</CardBottomNote>}
              </CardBottomRow>
            )}
            {lock}
          </TransactionCard>
        );
      })}
    </CardsList>
  );

  const renderContent = () => {
    if (isLoading && transactions.length === 0) {
      return renderSkeleton;
    }
    if (transactions.length === 0) {
      return renderEmpty;
    }
    return isCompact ? renderMobileCards() : renderDesktopRows();
  };

  return (
    <SectionCard>
      <SectionHeader>
        <TitleBlock>
          <SectionTitle>Recent transactions</SectionTitle>
          {transactions.length > 0 && (
            <SubtitleText>Last {transactions.length} entries</SubtitleText>
          )}
        </TitleBlock>
        <HeaderActions>
          {totalRows > 0 && (
            <ViewAllLink
              type={ButtonTypes.link}
              onClick={onViewAll}
              aria-label={`View all ${totalRows} transactions`}
            >
              View all {totalRows} →
            </ViewAllLink>
          )}
          <Button
            type={ButtonTypes.primary}
            size={addButtonSize}
            onClick={onAdd}
            aria-label="Add a new transaction"
          >
            <span aria-hidden="true">+</span>
            <AddButtonFullLabel>New transaction</AddButtonFullLabel>
            <AddButtonShortLabel>Add</AddButtonShortLabel>
          </Button>
        </HeaderActions>
      </SectionHeader>

      {renderContent()}

      {totalRows > 0 && transactions.length > 0 && (
        <CardFooterLink
          isMobileOnly
          onClick={onViewAll}
          aria-label={`View all ${totalRows} transactions`}
        >
          View all {totalRows} →
        </CardFooterLink>
      )}

      <TransactionActionSheet
        transaction={actionSheetTx}
        isOpen={actionSheetTx !== null}
        onClose={closeActionSheet}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
    </SectionCard>
  );
};
